'use client';

import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { Separator } from '../ui/separator';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { DialogDescription, DialogTitle } from '@eliadmin/core/src/components/ui/dialog';
import { Button } from '../ui/button';
import { AttributeSide } from './attributes/attribute';
import { Heading } from '../ui/heading';
import { ModalClose } from '../ui/modal';
import { LuX } from 'react-icons/lu';
import { useCategoryFilterContext } from './lazy-category-provider';
import { CategoryFilterContextPlaceholder } from './category-filter-context-placeholder';
import { ProgressiveCategoryAttributes } from './progressive-category-attributes';

const FilterSeparator = () => <Separator className="w-full last-of-type:hidden" />;

export function CategoryFilterContent() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const category = useCategoryStore(state => state.category);
  const clearFilters = useCategoryStore(state => state.clearFilters);
  const { t } = useTranslations();
  const { status } = useCategoryFilterContext();

  const attributes = category.attributes?.filter(
    attribute => attribute.name && attribute.values?.length,
  );

  if (status !== 'ready') {
    return (
      <Card className={cn('relative w-full', 'ea-category-filter-sheet')}>
        <DialogTitle className="sr-only">{t('text174')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('text1380')} · {t('text3384')}
        </DialogDescription>
        <div className="flex w-full items-center justify-between pb-6">
          <Heading title={t('text174')} tag="h4" />
          <ModalClose className="text-border" aria-label={t('text272')}>
            <LuX size={20} className="text-border" />
          </ModalClose>
        </div>
        <CategoryFilterContextPlaceholder />
      </Card>
    );
  }

  return (
    <Card className={cn('relative w-full', 'ea-category-filter-sheet')}>
      <DialogTitle className="sr-only">{t('text174')}</DialogTitle>
      <DialogDescription className="sr-only">
        {t('text1380')} · {t('text3384')}
      </DialogDescription>
      <div className="flex w-full items-center justify-between pb-6">
        <Heading title={t('text174')} tag="h4" />
        <ModalClose className="text-border" aria-label={t('text272')}>
          <LuX size={20} className="text-border" />
        </ModalClose>
      </div>

      <div className={cn('mb-6 flex flex-col gap-4', 'ea-category-filter-sheet__content')}>
        {/* manufacturers */}
        {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
          !!category.manufacturers?.length && (
            <>
              <AttributeSide
                name={t('text195')}
                type="generic"
                attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                order={templateSettings.category.manufacturers_in_filter.order}
                className={{ wrapper: cn('', 'ea-category-filter-sheet__manufacturers') }}
              />
              <FilterSeparator />
            </>
          )}

        {/* statuses */}
        {!!templateSettings.category?.statuses_in_filter?.enabled &&
          !!category.statuses?.length && (
            <>
              <AttributeSide
                name={t('text518')}
                type="generic"
                attribute={{ type: 'status', type2: 'statuses' }}
                order={templateSettings.category?.categories_in_filter?.order}
                className={{ wrapper: cn('', 'ea-category-filter-sheet__statuses') }}
              />
              <FilterSeparator />
            </>
          )}

        {/* availabilities */}
        {!!templateSettings.category?.availability_in_filter?.enabled &&
          !!category.availabilities?.length && (
            <>
              <AttributeSide
                name={t('text339')}
                type="generic"
                attribute={{ type: 'availability', type2: 'availabilities' }}
                order={templateSettings.category.availability_in_filter.order}
                className={{ wrapper: cn('', 'ea-category-filter-sheet__availabilities') }}
              />
              <FilterSeparator />
            </>
          )}

        {/* attributes */}
        {!!templateSettings.category?.attributes_in_filter?.enabled && (
          // <div className={`order-${templateSettings.category.attributes_in_filter.order}`}>
          <>
            <ProgressiveCategoryAttributes
              attributes={attributes ?? []}
              initialCount={10}
              className={cn('', 'ea-category-filter-sheet__attributes')}
              separatorClassName="w-full last-of-type:hidden"
            />
          </>
        )}

        {/* price */}
        {!!templateSettings.category?.price_in_filter?.enabled && (
          <>
            <AttributeSide
              name={t('text340')}
              type="price"
              attribute={{ type: 'status', type2: 'statuses' }}
              order={templateSettings.category.price_in_filter.order}
              className={{ wrapper: cn('', 'ea-category-filter-sheet__price') }}
            />
            <FilterSeparator />
          </>
        )}

        {!!templateSettings.category?.suppliers_in_filter?.enabled && (
          <>
            <AttributeSide
              name={t('text718')}
              type="generic"
              attribute={{ type: 'supplier', type2: 'suppliers' }}
              className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers') }}
              order={templateSettings.category.suppliers_in_filter.order}
            />
            <FilterSeparator />
          </>
        )}
        {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
          <>
            <AttributeSide
              name={t('text728')}
              type="supplierLocation"
              attribute={{ type: 'supplier', type2: 'suppliers' }}
              className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
              order={templateSettings.category?.suppliers_in_filter?.order}
            />
            <FilterSeparator />
          </>
        )}
      </div>

      <div className="sticky bottom-0 flex flex-1 flex-col items-center justify-center gap-6 bg-background pb-2">
        <ModalClose asChild>
          <Button type="button" className="w-full">
            {t('text1380')}
          </Button>
        </ModalClose>
        <ModalClose asChild>
          <Button
            type="button"
            variant={'none'}
            size={'none'}
            className="text-[#666666]"
            onClick={clearFilters}
          >
            {t('text3384')}
          </Button>
        </ModalClose>
      </div>
    </Card>
  );
}
